import React, { useCallback, useEffect, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppPillSwitch } from '../../components/ui';
import tempsService from '../../services/tempsService';
import { notify } from '../../components/common/AppToast';
import { formatIsoDate, mondayOf } from '../../utils/tempsWeek';
import TempsPlanningBoard from './TempsPlanningBoard';
import TempsEquipePanel from './TempsEquipePanel';
import TempsCommandesBoard from './TempsCommandesBoard';
import TempsModelesBoard from './TempsModelesBoard';
import '../../styles/temps-pilotage.scss';

const VIEWS = [
  { value: 'semaine', label: 'Semaine' },
  { value: 'commandes', label: 'Commandes' },
  { value: 'modeles', label: 'Modèles' },
];

const STATUS_LABELS = {
  a_faire: 'À faire',
  validee: 'Validée',
  archivee: 'Archivée',
};

const formatRelativeSync = (ts) => {
  if (!ts) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (diffSec < 60) return 'à l’instant';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return `il y a ${days} j`;
};

const summarizeOrderTasks = (order) => {
  let todo = 0;
  let done = 0;
  let unassigned = 0;
  let total = 0;
  (order.lines || []).forEach((line) => {
    (line.tasks || []).forEach((task) => {
      total += 1;
      if (task.status === 'a_faire') {
        todo += 1;
        if (!task.assignee) unassigned += 1;
      } else if (task.status === 'validee') {
        done += 1;
      }
    });
  });
  return { todo, done, unassigned, total };
};

const withUpdatedTaskSummary = (order) => ({
  ...order,
  taskSummary: summarizeOrderTasks(order),
});

const patchTaskInOrders = (orders, updatedTask) => orders.map((order) => {
  if (Number(order.id) !== Number(updatedTask.commandeId)) return order;
  const lines = (order.lines || []).map((line) => ({
    ...line,
    tasks: (line.tasks || []).map((task) => (
      Number(task.id) === Number(updatedTask.id) ? { ...task, ...updatedTask } : task
    )),
  }));
  return withUpdatedTaskSummary({ ...order, lines });
});

const appendTaskInOrders = (orders, createdTask) => orders.map((order) => {
  if (Number(order.id) !== Number(createdTask.commandeId)) return order;
  const ligneId = createdTask.ligneId;
  let found = false;
  let lines = (order.lines || []).map((line) => {
    if (ligneId != null && Number(line.id) === Number(ligneId)) {
      found = true;
      return { ...line, tasks: [...(line.tasks || []), createdTask] };
    }
    return line;
  });
  if (!found) {
    lines = [
      ...lines,
      {
        id: ligneId,
        productRef: createdTask.productRef || '',
        productLabel: createdTask.productLabel || 'Tâches ajoutées',
        qty: null,
        brands: { iti: false, kkp: false, klub: false },
        tasks: [createdTask],
      },
    ];
  }
  return withUpdatedTaskSummary({ ...order, lines });
});

const removeTaskFromOrders = (orders, taskId) => orders.map((order) => {
  const lines = (order.lines || []).map((line) => ({
    ...line,
    tasks: (line.tasks || []).filter((task) => Number(task.id) !== Number(taskId)),
  }));
  return withUpdatedTaskSummary({ ...order, lines });
});

const TempsPilotagePage = () => {
  const [view, setView] = useState('semaine');
  const [semaineSub, setSemaineSub] = useState('planning');
  const [assignFilter, setAssignFilter] = useState('all');
  const [week, setWeek] = useState(formatIsoDate(mondayOf()));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState({ people: [], unscheduled: [], remindLater: [] });
  const [teamStats, setTeamStats] = useState({
    members: [],
    unassignedTodoCount: 0,
    unassignedWeekTodoCount: 0,
    unscheduledTodoCount: 0,
  });
  const [orders, setOrders] = useState([]);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [pendingFlags, setPendingFlags] = useState({});
  const [pendingStatuses, setPendingStatuses] = useState({});
  const [products, setProducts] = useState([]);
  const [, setTick] = useState(0);

  const loadUsers = useCallback(async () => {
    try {
      const data = await tempsService.listProdUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setUsers([]);
      notify.error(e?.message || 'Impossible de charger les personnes Production');
    }
  }, []);

  const fetchViewData = useCallback(async () => {
    if (view === 'semaine') {
      if (semaineSub === 'equipe') {
        setTeamStats(await tempsService.weekTeam(week));
      } else {
        setBoard(await tempsService.weekBoard(week));
      }
    } else if (view === 'commandes') {
      const data = await tempsService.listCommandes({ scope: 'all' });
      setOrders(Array.isArray(data) ? data : []);
    } else {
      const data = await tempsService.listProducts();
      setProducts(Array.isArray(data) ? data : []);
    }
  }, [view, semaineSub, week]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await fetchViewData();
    } catch (e) {
      notify.error(e?.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [fetchViewData]);

  const reloadSilent = useCallback(async () => {
    try {
      await fetchViewData();
    } catch (e) {
      notify.error(e?.message || 'Mise à jour impossible');
    }
  }, [fetchViewData]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!lastSyncAt || view !== 'commandes') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [lastSyncAt, view]);

  const toggleConcernsProd = async (order, next) => {
    setPendingFlags((prev) => ({ ...prev, [order.id]: true }));
    try {
      await tempsService.setCommandeFlag(order.id, next);
      setOrders((prev) => prev.map((o) => (
        o.id === order.id ? { ...o, concernsProd: next } : o
      )));
      notify.success(next ? 'Commande marquée pour la prod' : 'Flag prod retiré');
    } catch (e) {
      notify.error(e?.message || 'Impossible de mettre à jour le flag prod');
    } finally {
      setPendingFlags((prev) => {
        const nextState = { ...prev };
        delete nextState[order.id];
        return nextState;
      });
    }
  };

  const syncDolibarrCommandes = async () => {
    setSyncingOrders(true);
    try {
      await tempsService.syncCommandes();
      setLastSyncAt(Date.now());
      notify.success('Synchronisation Dolibarr terminée');
      await reloadSilent();
    } catch (e) {
      notify.error(e?.message || 'Synchronisation Dolibarr impossible');
    } finally {
      setSyncingOrders(false);
    }
  };

  const createOrderTask = async (payload) => {
    try {
      const created = await tempsService.createTask({
        title: payload.title,
        estimatedMinutes: Number(payload.estimatedMinutes) || 0,
        assigneeId: payload.assigneeId || null,
        weekStart: payload.weekStart || null,
        commandeId: payload.commandeId,
        ligneId: payload.ligneId,
      });
      if (created?.id) {
        setOrders((prev) => appendTaskInOrders(prev, created));
      } else {
        await reloadSilent();
      }
      notify.success('Tâche ajoutée');
      return created;
    } catch (e) {
      notify.error(e?.message || 'Tâche non enregistrée');
      throw e;
    }
  };

  const changeTaskStatus = async (task, status) => {
    setPendingStatuses((prev) => ({ ...prev, [task.id]: true }));
    try {
      const updated = await tempsService.setTaskStatus(task.id, status);
      if (updated?.id) {
        setOrders((prev) => patchTaskInOrders(prev, updated));
      } else {
        await reloadSilent();
      }
      notify.success(`Statut : ${STATUS_LABELS[status] || status}`);
    } catch (e) {
      const msg = e?.message || 'Impossible de changer le statut';
      notify.error(msg);
      if (/404|introuvable/i.test(msg)) {
        await reloadSilent();
      }
    } finally {
      setPendingStatuses((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
    }
  };

  const archiveOrderValidated = async (order) => {
    try {
      await tempsService.archiveOrder(order.id);
      notify.success('Tâches validées archivées');
      await reloadSilent();
    } catch (e) {
      notify.error(e?.message || 'Archivage impossible');
      throw e;
    }
  };

  const createTemplate = async (payload) => {
    try {
      const created = await tempsService.createTemplate(payload);
      notify.success('Modèle ajouté');
      await reloadSilent();
      return created;
    } catch (e) {
      notify.error(e?.message || 'Modèle non enregistré');
      throw e;
    }
  };

  const deleteTemplate = async (template) => {
    try {
      await tempsService.deleteTemplate(template.id);
      notify.success('Modèle supprimé');
      await reloadSilent();
    } catch (e) {
      notify.error(e?.message || 'Suppression impossible');
      throw e;
    }
  };

  const saveAssignment = async (task, patch, successMessage) => {
    try {
      await tempsService.updateTask(task.id, patch);
      if (successMessage) notify.success(successMessage);
      await reloadSilent();
    } catch (e) {
      notify.error(e?.message || 'Modification impossible');
    }
  };

  const setAssignee = (task, assigneeId) => {
    const id = assigneeId == null ? null : Number(assigneeId);
    const current = task.assignee?.id ?? null;
    if (current === id) return;
    const name = users.find((u) => Number(u.id) === id)?.name;
    saveAssignment(
      task,
      { assigneeId: id },
      id == null ? 'Tâche non assignée' : `Assignée à ${name || 'la personne'}`,
    );
  };

  const setSchedule = (task, schedule) => {
    const labels = {
      this_week: 'Planifiée cette semaine',
      next_week: 'Planifiée semaine +1',
      remind_later: 'Reportée — me rappeler plus tard',
    };
    saveAssignment(task, { schedule, weekRef: week }, labels[schedule] || 'Planning mis à jour');
  };

  const archiveTask = async (task) => {
    try {
      await tempsService.archiveTask(task.id);
      notify.success('Tâche archivée');
      await reloadSilent();
    } catch (e) {
      notify.error(e?.message || 'Archivage impossible');
    }
  };

  const deleteTask = async (task) => {
    try {
      await tempsService.deleteTask(task.id);
      if (view === 'commandes') {
        setOrders((prev) => removeTaskFromOrders(prev, task.id));
      } else {
        await reloadSilent();
      }
      notify.success('Tâche supprimée');
    } catch (e) {
      notify.error(e?.message || 'Suppression impossible');
    }
  };

  const equipeContent = (
    <TempsEquipePanel
      teamStats={teamStats}
      loading={loading && semaineSub === 'equipe'}
      onGoPlanningUnassigned={() => {
        setSemaineSub('planning');
        setAssignFilter('unassigned');
      }}
    />
  );

  const lastSyncLabel = lastSyncAt
    ? `Dernière sync · ${formatRelativeSync(lastSyncAt)}`
    : '';

  return (
    <MasterLayout>
      <Breadcrumb title="Pilotage" />
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <AppPillSwitch variant="segment" options={VIEWS} value={view} onChange={setView} ariaLabel="Vues du pilotage" />
      </div>
      {view === 'semaine' ? (
        <TempsPlanningBoard
          week={week}
          onWeekChange={setWeek}
          semaineSub={semaineSub}
          onSemaineSubChange={setSemaineSub}
          assignFilter={assignFilter}
          onAssignFilterChange={setAssignFilter}
          board={board}
          users={users}
          loading={loading}
          onAssignee={setAssignee}
          onSchedule={setSchedule}
          onArchive={archiveTask}
          onDelete={deleteTask}
          equipeContent={equipeContent}
        />
      ) : view === 'commandes' ? (
        <TempsCommandesBoard
          orders={orders}
          loading={loading}
          syncing={syncingOrders}
          lastSyncLabel={lastSyncLabel}
          pendingFlags={pendingFlags}
          pendingStatuses={pendingStatuses}
          onSync={syncDolibarrCommandes}
          onToggleProd={toggleConcernsProd}
          onArchive={archiveOrderValidated}
          onCreateTask={createOrderTask}
          onChangeStatus={changeTaskStatus}
          onDeleteTask={deleteTask}
        />
      ) : (
        <TempsModelesBoard
          products={products}
          users={users}
          loading={loading}
          onCreateTemplate={createTemplate}
          onDeleteTemplate={deleteTemplate}
        />
      )}
    </MasterLayout>
  );
};

export default TempsPilotagePage;
